import { useState } from 'react';
import { X } from 'react-bootstrap-icons';

import { SDSButton } from '@repo/sds/SDSButton';
import { SDSCheckbox } from '@repo/sds/SDSCheckbox';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSIconButton } from '@repo/sds/SDSIconButton';
import { SDSPopover } from '@repo/sds/SDSPopover';
import { SDSText } from '@repo/sds/SDSText';

import { DAYS_OF_WEEK } from '../../constants/time';

import './CopyDaysPopover.scss';

interface CopyDaysPopoverProps {
  anchorEl: HTMLButtonElement | null;
  onClose: () => void;
  onApply: (selectedDays: string[]) => void;
  day: string;
}

export function CopyDaysPopover({ anchorEl, onClose, onApply, day }: CopyDaysPopoverProps) {
  const [selectedDays, setSelectedDays] = useState<string[]>([]);

  const handleDayToggle = (toggleDay: string, isChecked: boolean) => {
    if (isChecked) {
      setSelectedDays(prev => {
        const newSelected = [...prev, toggleDay];
        return newSelected;
      });
    } else {
      setSelectedDays(prev => {
        const newSelected = prev.filter(d => d !== toggleDay);
        return newSelected;
      });
    }
  };

  const handleApply = () => {
    onApply(selectedDays);
    onClose();
    setSelectedDays([]);
  };

  const open = Boolean(anchorEl);

  return (
    <SDSPopover
      open={open}
      anchor={anchorEl}
      onOpenChange={isOpen => !isOpen && onClose()}
      side="bottom"
      align="end"
      content={
        <SDSFlexbox className="CopyDaysPopover" direction="column" gap="content-default" justifyContent="flex-end">
          <SDSFlexbox direction="column" gap="text-x-large" alignItems="flex-start">
            <SDSFlexbox justifyContent="space-between" alignItems="flex-start" fullWidth>
              <SDSText className="CopyDaysPopover__title" text="Copy to" type="h5" />
              <SDSIconButton icon={<X />} onClick={onClose} ariaLabel="Close copy days popover" />
            </SDSFlexbox>
            <SDSFlexbox direction="column" gap="text-large" alignItems="flex-start">
              {DAYS_OF_WEEK.map(
                dayOption =>
                  dayOption !== day && (
                    <SDSFlexbox gap="text-large" alignItems="center" key={dayOption}>
                      <SDSCheckbox
                        isChecked={selectedDays.includes(dayOption)}
                        onChange={isChecked => handleDayToggle(dayOption, isChecked)}
                        ariaLabel={`Copy time slots to ${dayOption}`}
                      />
                      <SDSText text={dayOption} type="body-extra-large" />
                    </SDSFlexbox>
                  ),
              )}
            </SDSFlexbox>
            <SDSFlexbox justifyContent="flex-end" fullWidth>
              <SDSButton
                onClick={handleApply}
                isDisabled={selectedDays.length === 0}
                className="CopyDaysPopover__apply"
                text="Apply"
                ariaLabel={`Apply time slots to ${selectedDays.length} selected days`}
              />
            </SDSFlexbox>
          </SDSFlexbox>
        </SDSFlexbox>
      }
    />
  );
}
